/** @module csi/action/_3DFileValidationMixin **/
define([
	"dojo/_base/declare",
	"dojo/dom-construct",
	"underscore",
	"csi",
	"csi/html",
	"csi/locale",
	"csi/model/document",
	"csi/reflection",
	"csi/types",
	"csi/util",
	"jszip",
], function(declare, domConstruct, _, csi, html, locale, {THREE_D_MATERIAL_SUBTYPE_URLS}, reflection, types, util, jszip) {

	const SOURCE_FILE = "SourceFile";
	const EXTENSIONS = "obj,fbx,glb,gltf,stl,3dm";
	const THREE_D_MATERIAL_EXTENSIONS = "u3m,fab,xtex,sbsar";
	const CLO_HARDWARE_EXTENSIONS = ["trm", "btn", "bth", "zpac", "sst", "zsp", "zpl", "zsd"];

	return declare(null, {
		EXTENSIONS,

		/** @property {string} docType - primary document type in the zip folder*/
		docType: null,

		/** @property {boolean} ignoreMtl - skips validating for mtl files in zip content*/
		ignoreMtl: false,

		/** @property {string} error message for missing object*/
		threeErrorMessage: null,

		/** @property {string} error message for invalid upload type*/
		threeInvalidTypeMessage: null,

		/** @property {string} warning message for secondary item missing*/
		threeMissingMessage: "",

		/** @property {object} file suffix*/
		threeFileSuffix: null,

		errInvalidZip: null,

		/** @property {boolean} determines if startup message row has already been created*/
		messageRowCreated: false,

		constructor: function() {
			this.errInvalidZip = locale.resource("Error3DInvalidZip");
		},

		/**
		 * Sets error msg
		 * @param {string} errMessage
		 * @param {string} errCode
		 */
		errorForm: function(errMessage, errCode) {
			var icon = !this.warnMsg ? this.invalidNode.ERROR : this.invalidNode.WARNING;
			this.invalidNode.set("icon", icon);
			if (this.warnMsg && this.invalidNode) {
				this.invalidNode.set("value", this.warnMsg);
				return false;
			} else {
				return this.inherited(arguments);
			}
		},

		/**
		 * Create the 3DUpload form
		 */
		onDrawCreateFileIndexNode: function() {
			var sourceFieldDom = this.getFieldRow(SOURCE_FILE);
			var sourceFieldMsgContainer = domConstruct.place(html.create("tr", {
				"class": "csi-3Dform-fileIndex-row"
			}), sourceFieldDom, "after");

			var sourceFieldMsgRow = html.create("td", {
				"colspan": "3"
			}, sourceFieldMsgContainer);

			this.fileIndexNode = html.create("div", {
				"class": "csi-3Dform-fileIndex-container"
			}, sourceFieldMsgRow);
		},

		/**
		 * Extension Point
		 * @param {boolean} [bIsRequired]
		 */
		on3DZipFile: function(bIsRequired, fileName) { },

		/**
		 * Validate a 3D zip file
		 * @param {file} fileToRead
		 */
		validate3DZip: function(fileToRead) {
			var self = this;
			var zipLoader = new jszip();
			var message = null;
			var zipContentList = [];
			var parentNode = this.getParent();

			if (!this.docType && parentNode.Subtype) {
				
				//set messages for uploader for inline edits
				if (parentNode.Subtype == csi.EMPTY_URL || parentNode.Subtype == reflection.URL_SUBTYPE_3D_VIEWABLE) {
					this._initializeFileType(EXTENSIONS);
				} else if (THREE_D_MATERIAL_SUBTYPE_URLS.includes(parentNode.Subtype)) {
					this._initializeFileType(THREE_D_MATERIAL_EXTENSIONS);
				}
			}

			return zipLoader.loadAsync(fileToRead).then(function(zip) {
				// process ZIP file content here
				var objExists = false;
				var sizeExceeded = false;
				var mtlExists = false;
				var zipFiles = zip.files;
				var requiredExtensions = [];
				var isCloHardwareFile = false;

				// Uploads for Clo hardware files don't have required extensions
				if (_.contains(CLO_HARDWARE_EXTENSIONS, util.getFileExtension(fileToRead.name))) {
					isCloHardwareFile = true;
				} else {
					requiredExtensions = self.docType.split(",");
				}

				var uploading3DMtl = requiredExtensions.length == 3 && !_.contains(requiredExtensions, "json");
				var matExtFound = false;
				var extensionsArray = uploading3DMtl ? ["u3m", "xtex", "fab"] : EXTENSIONS.split(",");
				let bHasTrims = false;

				Object.keys(zip.files).forEach(function(filename, i, arr) {
					var ext = util.getFileExtension(filename);
					if (ext === "fbx" || ext === "obj") {
						bHasTrims = true;
					}
					var testExtensions = _.contains(requiredExtensions, ext);
					var bRequired = testExtensions || (ext === "mtl" && !self.ignoreMtl);
					matExtFound = matExtFound || ext == "u3m" || ext == "fab" || ext == "xtex";
					objExists = objExists || testExtensions;
					mtlExists = self.ignoreMtl ? false : (mtlExists || (ext === "mtl"));

					if (testExtensions) {
						//Different forms will remove different extensions from the array when one of the extensions is found
						if (_.contains(extensionsArray, ext)) {
							if (ext === "fbx") {
								self.ignoreMtl = true;
							}
							requiredExtensions = _.difference(requiredExtensions, extensionsArray);
						} else {
							requiredExtensions = _.without(requiredExtensions, ext);
						}
					}

					if (testExtensions && zipFiles[filename]._data.uncompressedSize > types.MAX_3DFILE_SIZE) {
						message = locale.resource("Warning3DFileOversize", [filename]);
						sizeExceeded = true;
					} else if (!sizeExceeded) {
						message = isCloHardwareFile == true ? "" : objExists == false ? self.threeErrorMessage : !mtlExists ? self.threeMissingMessage : "";
					}

					var cleanFileName = filename.split("/").pop();

					zipContentList.push({
						Name: filename,
						ext: ext
					});
					//Do not want to call on3DZip if the current file is a dir
					if (cleanFileName) {
						if (testExtensions && self.threeFileSuffix) {
							// key couldn't start with a number, so if the extension is '3dm' then we need to look up 'threedm' instead
							var extensionText = ext === "3dm" ? self.threeFileSuffix["threedm"] : self.threeFileSuffix[ext];
							self.on3DZipFile(bRequired, cleanFileName + (" - " + extensionText));
						} else {
							self.on3DZipFile(bRequired, cleanFileName);
						}
					}
				});

				// If the zip file contains .fbx or .obj files, then hide the checkbox
				self.hideCheckboxField(bHasTrims);

				//A form is considered valid if a warning exists but not if an error does.
				var bWarning = (objExists && (!mtlExists || sizeExceeded));

				if (requiredExtensions.length) {
					message = self.threeErrorMessage;
					bWarning = false;
				}
				if (!message && !uploading3DMtl && matExtFound) {
					message = self.threeInvalidTypeMessage;
					bWarning = false;
				}

				return {
					message,
					bWarning,
					contents: zipContentList,
					zip
				};
			}, function() {
				message = !fileToRead ? locale.resource("Error3DNoFile") : self.errInvalidZip;
				return {message: message};
			});
		},

		/**
		 * Initializes messages and doctype for uploading 3d models, 3d materials and physics files
		 * @param {string} type
		 */
		_initializeFileType: function(type) {
			this.docType = type;

			if (type === EXTENSIONS) {
				if (type == 'obj') {
					this.threeMissingMessage = locale.resource("Warning3DMtlMissing");
				}
				this.threeErrorMessage = locale.resource("Error3DObjMissing");
				this.threeInvalidTypeMessage = locale.resource("CannotLoadMaterialArchiveAsDocument");
				this.threeFileSuffix = {
					fbx: locale.resource("3DFbx"),
					glb: locale.resource("3DGlb"),
					gltf: locale.resource("3DGltf"),
					obj: locale.resource("3DObj"),
					stl: locale.resource("3DStl"),
					threedm: locale.resource("3D3dm")
				};
			} else if (type === THREE_D_MATERIAL_EXTENSIONS) {
				this.threeMissingMessage = "";
				this.threeErrorMessage = locale.resource("Error3DU3MMissing");
				this.threeFileSuffix = {u3m: locale.resource("3DU3M"), xtex: locale.resource("3DXTEX"), fab: locale.resource("3DFAB"), sbsar:locale.resource("3DSBSAR")};
			} else if (type === "json,obj") {
				this.threeErrorMessage = locale.resource("Error3DPhysicsMissing");
				this.threeFileSuffix = {
					json: locale.resource("3DPhysics"),
					obj: locale.resource("3DObj")
				};
			}
		},

		/**
		 * On file select check if it has Trims, update checkbox visibility
		 * @param {boolean} bHasTrims
		 */
		hideCheckboxField: function(bHasTrims) {

			// File validation can occur outside of a form, so the field is not always available
			if (this.setFieldHidden) {
				this.setFieldHidden("3DPropagateToExistingColorMaterials", bHasTrims);
			}
		}
	})
})